import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { string, object } from 'prop-types';
import { Tabs, Empty, Badge } from 'antd';
import { Card } from '@cards/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import CallToActionForm from '@cards/CallToAction/Form';
import './style.less';

const { TabPane } = Tabs;

const openExternalLink = link => {
  window.open(link, '_blank');
};

const getMarkdownListFromItems = items => {
  return items.map(item => `- [${item.name}](${item.link})`).join('\n\n');
};

const ProductTabTitle = ({ count, t }) => {
  if (count === 0) return t('cards:CallToActionFormWithItemDB.productsTab');
  return (
    <Badge count={count}>
      {t('cards:CallToActionFormWithItemDB.productsTab')}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    </Badge>
  );
};

const ProductList = ({ products, handleRemoveProduct, t }) => {
  if (products.length === 0)
    return (
      <div className="call-to-action-form-with-itemdb-product-list">
        <h2>{t('cards:CallToActionFormWithItemDB.selectedProducts')}:</h2>
        <div className="call-to-action-form-with-itemdb-product-card">
          <span>{t('cards:CallToActionFormWithItemDB.noProductsSelected')}</span>
        </div>
      </div>
    );
  return (
    <div className="call-to-action-form-with-itemdb-product-list">
      <h2>{t('cards:CallToActionFormWithItemDB.selectedProducts')}:</h2>
      {products.map(product => (
        <ProductCard
          key={product.id}
          product={product}
          handleRemoveProduct={() => handleRemoveProduct(product.id)}
          openExternalLink={openExternalLink}
        />
      ))}
    </div>
  );
};

const ProductCard = ({ product, handleRemoveProduct, openExternalLink }) => {
  return (
    <div className="call-to-action-form-with-itemdb-product-card">
      <span>
        {product.name}{' '}
        <FontAwesomeIcon icon={faExternalLink} onClick={() => openExternalLink(product.link)} />{' '}
        <FontAwesomeIcon icon={faTrashAlt} onClick={() => handleRemoveProduct(product.id)} />
      </span>
    </div>
  );
};

function CallToActionFormWithItemDB({
  appUserId,
  contentRef,
  title,
  search_experience_slug,
  ctaData,
}) {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState([]);
  const [hasInitialisedIframe, setHasInitialisedIframe] = useState(false);
  const [formDataCached, setFormDataCached] = useState(null);
  const iframeUrl = ctaData?.iframe_url;
  const formRef = useRef(null);

  const additionalFormDataToSubmit = useMemo(() => {
    return {
      'item-db-data': selectedItems,
      'item-db-text': getMarkdownListFromItems(selectedItems),
    };
  }, [selectedItems]);

  const handleItemSelected = useCallback(event => {
    if (event.data.type === 'item-db-item-clicked') {
      setSelectedItems(prevItems => {
        if (prevItems.some(item => item.id === event.data.item.id)) return prevItems;
        return [...prevItems, event.data.item];
      });
    }
  }, []);

  const handleRemoveProduct = useCallback(
    productId => {
      setSelectedItems(prevItems => prevItems.filter(item => item.id !== productId));
    },
    [setSelectedItems],
  );

  useEffect(() => {
    if (hasInitialisedIframe) return;
    setHasInitialisedIframe(true);
    window.addEventListener('message', handleItemSelected);
    // return () => {
    //   window.removeEventListener('message', handleItemSelected);
    // };
  }, [hasInitialisedIframe, setHasInitialisedIframe, handleItemSelected]);

  const handleTabChange = useCallback(
    key => {
      const formData = formRef.current?.state.formData;
      if (formData) {
        setFormDataCached(formData);
      }
    },
    [formRef],
  );

  return (
    <Card className="call-to-action-form-with-itemdb-card" title={title} loading={false}>
      <Tabs defaultActiveKey="form" onChange={handleTabChange}>
        <TabPane tab={t('cards:CallToActionFormWithItemDB.formTab')} key="form">
          <CallToActionForm
            appUserId={appUserId}
            contentRef={contentRef}
            title={title}
            additionalFormDataToSubmit={additionalFormDataToSubmit}
            formRef={formRef}
            formDataCached={formDataCached ? formDataCached : undefined}
          />
        </TabPane>
        <TabPane tab={<ProductTabTitle count={selectedItems.length} t={t} />} key="products">
          {iframeUrl ? (
            <>
              <ProductList
                products={selectedItems}
                handleRemoveProduct={handleRemoveProduct}
                t={t}
              />
              <div className="call-to-action-form-with-itemdb-iframe-container">
                <iframe
                  src={iframeUrl}
                  title={t('cards:CallToActionFormWithItemDB.productsTab')}
                  style={{ width: '100%', border: 'none' }}
                />
              </div>
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('cards:CallToActionFormWithItemDB.iframeUrlMissing')}
            />
          )}
        </TabPane>
      </Tabs>
    </Card>
  );
}

CallToActionFormWithItemDB.propTypes = {
  appUserId: string.isRequired,
  contentRef: string.isRequired,
  title: string,
  search_experience_slug: string,
  ctaData: object,
};

CallToActionFormWithItemDB.defaultProps = {
  title: undefined,
  search_experience_slug: undefined,
  ctaData: undefined,
};

export default CallToActionFormWithItemDB;
